// native
import { Component, OnInit } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { BehaviorSubject } from 'rxjs';
import { DatePipe } from '@angular/common';
import { finalize } from 'rxjs/operators';

// addon
import { TranslateService } from '@ngx-translate/core';

// service
import { TestsRecurringService } from 'app/core/services/tests-recurring.service';
import { PatientsService } from 'app/core/services/patients.service';
import { ErrorService } from 'app/core/services/error.service';
import { DialogService } from 'app/core/services/dialog.service';
import { TabsService } from 'app/core/services/tabs.service';
import { PreferencesService } from 'app/core/services/preferences.service';
import { AuthService } from 'app/core/services/auth.service';

// models
import { PaginatedItems, Tab, RecurringTest, TestGroupInfo, ListItemAction, ListItemColumn, BreadcrumbOptions } from '../../models';

// animation
import { routerTransition } from '../../animations/router-animations';

// constants
import { DEFAULT_PAGE_INDEX, DEFAULT_PAGE_SIZE, TEST_TAB_INDEX } from '../../constants';

interface RecurringTestDisplay extends RecurringTest {
  test_group_info: string;
}

@Component({
    selector: 'app-tests',
    templateUrl: './tests-recurring.component.html',
    animations: [routerTransition()],
    standalone: false
})
export class TestsRecurringComponent implements OnInit {
  columns: ListItemColumn[] = [
    {
      translationKey: 'testGroupStrategyProtocol',
      fieldName: 'test_group_info',
      fontClass: 'bold'
    },
    {
      translationKey: 'periodInDays',
      fieldName: 'period_in_days',
      fontClass: 'bold'
    },
    {
      translationKey: 'nextTestDate',
      fieldName: 'next_test_date',
      fontClass: 'thin'
    },
    {
      translationKey: 'endDate',
      fieldName: 'end_date',
      fontClass: 'thin'
    }
  ];

  actions: ListItemAction[] = [
    {
      translationKey: 'edit',
      execute: (test: RecurringTestDisplay) => this.edit(test),
      dataCy: (test: RecurringTestDisplay) => `recurring-test-edit-${test.id}`,
      visible: () => !this.authService.isClinicalTrialReadOnlyNoPii
    },
    {
      translationKey: 'delete',
      execute: (test: RecurringTestDisplay) => this.delete(test),
      dataCy: (test: RecurringTestDisplay) => `recurring-test-delete-${test.id}`,
      visible: () => !this.authService.isClinicalTrialReadOnlyNoPii
    }
  ];

  breadcrumbOptions: BreadcrumbOptions = {
    stepLabels: ['patients'],
    stepActions: [() => { this.router.navigate(['/patients']); }]
  };

  tabs: Tab[] = [];

  patientId: number;

  testsRecurring$: BehaviorSubject<PaginatedItems<RecurringTestDisplay> | null> = new BehaviorSubject(null);

  currentPageSize: number = DEFAULT_PAGE_SIZE;
  currentPageIndex: number = DEFAULT_PAGE_INDEX;
  term: string;

  isLoading: boolean = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private testsRecurringService: TestsRecurringService,
    private patientsService: PatientsService,
    private translateService: TranslateService,
    private errorService: ErrorService,
    private dialogService: DialogService,
    private tabsService: TabsService,
    private preferencesService: PreferencesService,
    private authService: AuthService
  ) { }

  ngOnInit() {
    this.patientId = this.route.snapshot.params['patientId'];

    this.tabs = this.tabsService.getTestTabs(this.patientId, TEST_TAB_INDEX.RECURRING_TESTS);

    this.getTests(this.patientId, this.currentPageSize, this.currentPageIndex, null);

    this.patientsService.getOne(this.patientId).subscribe(patient => {
      this.breadcrumbOptions.stepLabels.push(this.patientsService.getDisplayName(patient));
    });
  }

  private getTests(patientId: number, pageSize: number, pageIndex: number, term: string) {
    this.isLoading = true;
    this.testsRecurringService.getAllTests(patientId, pageSize, pageIndex, term).pipe(
      finalize(() => this.isLoading = false)
    ).subscribe(
      tests => this.testsRecurring$.next(this.parseTests(tests)),
      error => this.errorService.handleError(error));
  }

  private parseTests(tests: PaginatedItems<RecurringTest>): PaginatedItems<RecurringTestDisplay> {
    const parsedTests = tests.results.map(recurringTest => ({
      ...recurringTest,
      test_group_info: this.getTestGroupDisplay(recurringTest.test?.test_group),
      next_test_date: new DatePipe('en-US').transform(recurringTest.next_test_date, this.preferencesService.defaultDateFormat.value),
      end_date: new DatePipe('en-US').transform(recurringTest.end_date, this.preferencesService.defaultDateFormat.value)
    }));

    return {
      results: parsedTests,
      count: tests.count
    };
  }

  private getTestGroupDisplay(group: TestGroupInfo) {
    let text = '';
    if (!group?.group)
      return text;
    if (group.group.value)
      text = text + group.group.name;
    if (group?.strategy?.value)
      text = text + ': ' + group.strategy.name;
    if (group?.protocol?.value)
      text = text + ': ' + group.protocol.name;

    return text;
  }

  view(test: RecurringTestDisplay) {
    this.router.navigate(['/tests/recurring/view', test.id], { queryParamsHandling: 'merge' });
  }

  edit(test: RecurringTestDisplay) {
    this.router.navigate(['/tests/recurring/edit', test.id], { queryParamsHandling: 'merge' });
  }

  delete(test: RecurringTestDisplay) {
    this.dialogService.openConfirm({
      action: this.translateService.instant('delete'),
      message: this.translateService.instant('deleteReccuringTestPrompt'),
      cancelText: this.translateService.instant('back')
    }).then(result => {
      if (result.confirmed)
        this.testsRecurringService.delete(test.id).subscribe(
          res => this.getTests(this.patientId, this.currentPageSize, this.currentPageIndex, this.term),
          error => this.errorService.handleError(error));
    });
  }

  changePage(pageIndex: number, pageSize: number) {
    this.currentPageIndex = pageIndex;
    this.currentPageSize = pageSize;
    this.getTests(this.patientId, pageSize, pageIndex, this.term);
  }

  search(term: string) {
    this.currentPageIndex = 0;

    this.testsRecurringService.getAllTests(this.patientId, this.currentPageSize, this.currentPageIndex, term).subscribe(res => {
      this.term = term;
      this.testsRecurring$.next(this.parseTests(res));
    });
  };
}
